import { Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import type { Exercise } from '@heraion/shared';
import {
  Chip,
  IconButton,
  SectionHeader,
  UnseenCue,
  useDisclosure,
  useSeenOnce,
} from '../index';

import { machineLabel } from './machineLabel';
import { useExerciseName } from './exerciseName';
import { useTheme } from './theme';
import type { WeightUnit } from './state/loggerDraft';
import type { DraftEntry } from './state/loggerDraftTypes';
import type { makeStyles } from './ExerciseSwapPicker.styles';
import { MachineTray } from './_MachineTray';
import { useMachineChoice } from './_useMachineChoice';

// Per-device, per-install: the ✕ pulses until it has been opened once. ⛔ Not
// on the Profile — what this install has already shown its user is not a fact
// about the athlete, and it would cost a cloud write per tap.
const REMOVE_GEAR_SEEN_KEY = 'jl.seen.declareGearUnavailable';

// EQ-1 — the "Current: <exercise>" line, and the only place the athlete can
// stay on this exercise and change WHICH MACHINE they are on.
//
// ⭐ Why it exists at all (found building the slice, 2026-08-13):
// equipment-model.md §8.4 names replace-exercise as the single gesture for
// correcting a wrong instance — but the swap picker filters the current
// exercise out of both its sections (similarity.ts:50 and the "All" filter),
// so the exercise you are actually on is unreachable there. The reducer has
// always supported the move (a same-kind replace keeps the logged sets and
// re-stamps the tag); the surface for it was missing.
//
// ⛔ Always present, machines known or not. Gating it on the list rows' "two
// or more" rule deadlocked the feature: the tray is the only place an
// instance is minted, and it never opened until two already existed.
//
// It is a labelled CHIP, not a glyph. The first version put a tag icon with a
// count in the corner and Antenore could not find it on the device ("è poco
// visibile"), which is the same failure as a control nobody notices: it
// collects nothing. The chip also answers the question the glyph could not —
// WHICH machine is set — by carrying its name instead of a number.
type Props = {
  current: Exercise;
  onPick: (next: Exercise, instance?: string | null) => void;
  /** What the entry being edited carries right now, draft included. */
  currentInstance: string | null;
  /** EQ-1 slice D — the session's place. Ranks the tray, never trims it. */
  orderByLocation: string | null;
  /** The open session's entries, so a machine loaded TODAY already shows its
   *  load signature instead of waiting for the workout to be saved. */
  liveEntries: readonly DraftEntry[];
  unit: WeightUnit;
  /** DS-42 — open with the machine tray already down. The entry point
   *  `EQ-1-DISCOVER` needs: its popup says "set a variant" and must land the
   *  athlete ON that gesture, not merely on the screen that contains it. Named
   *  after `DisclosureCard`'s prop of the same job so the convention for
   *  "pre-open a disclosure from outside" stays one word app-wide. */
  initialExpanded?: boolean;
  /** EQ-1-GYM-COLDSTART R3 — opens the "which of these is not here?" dialog.
   *  ⛔ Omitted when the session has no place: the declaration is keyed by the
   *  place, so with none there is nothing to key and the ✕ must not exist
   *  rather than exist and do nothing. */
  onDeclareUnavailable?: () => void;
  /** R6 — machines declared absent HERE. They are not offered in the tray;
   *  they still own their letter, so a new one cannot recycle it. */
  absentHere?: readonly string[];
  /** EQ-1-PIN-DEFAULT — the ★ on each tray row. ⛔ Omitted when the place is
   *  unknown, for the same reason the ✕ is: an election has nowhere to live. */
  pin?: {
    pinned: string | null | undefined;
    onToggle: (value: string | null) => void;
  };
  theme: ReturnType<typeof useTheme>;
  styles: ReturnType<typeof makeStyles>;
};

export function CurrentExerciseMachineLine({
  current,
  onPick,
  currentInstance,
  orderByLocation,
  liveEntries,
  unit,
  initialExpanded = false,
  onDeclareUnavailable,
  absentHere,
  pin,
  theme,
  styles,
}: Props) {
  const { t } = useTranslation();
  const getExerciseName = useExerciseName();
  const disclosure = useDisclosure(initialExpanded);
  // R7 — the ✕ is one glyph, no words, and the ONLY door into the whole
  // declared-absence feature, on a row Antenore refused to widen with a label
  // (it already carries the exercise name and the gear chip, and it has
  // truncation history). So it announces itself by breathing until it has been
  // opened once, then never again — the same treatment the coach-notes "?" has
  // had since June, now one shared primitive.
  //
  // ⛔ One flag for the install, not one per exercise or per place: what is
  // being taught is that the control EXISTS, and that is learned once.
  // ⚠️ Opening it counts as seen even if the athlete then cancels the dialog.
  // Seeing it is what we are teaching; using it is their business.
  const { seen: removeSeen, markSeen: markRemoveSeen } =
    useSeenOnce(REMOVE_GEAR_SEEN_KEY);
  const { known, fallback, countLabel, takenValues } = useMachineChoice(
    current.slug,
    {
      draftInstance: currentInstance,
      orderByLocation,
      liveEntries,
      ...(absentHere ? { absentHere } : {}),
      // EQ-1-PIN-DEFAULT — an election ends the question for this exercise.
      ...(pin && pin.pinned !== undefined ? { pinned: pin.pinned } : {}),
    },
  );

  // DS-42 — the chip says WHICH JOB it does, not just its current value. The
  // key rides only when there is a value to key: "Gear: Machine A" reads, and
  // "Gear: Set machine" does not, so the unset state keeps the plain call to
  // action and leans on the band heading above for its naming.
  const machineName = currentInstance ? machineLabel(currentInstance, t) : null;
  const chipText =
    machineName === null
      ? t('swap.setMachine', { defaultValue: 'Set machine' })
      : `${t('swap.equipmentKey', { defaultValue: 'Gear' })}: ${machineName}`;

  return (
    <View>
      <SectionHeader
        theme={theme}
        title={t('swap.bandCurrentExercise', { defaultValue: 'This exercise' })}
        style={styles.bandHeader}
      />
      <View style={styles.currentLine}>
        {/* ⚠️ `currentLabelInRow` (the shrink) belongs to the label INSIDE this
            row, where it lets a long exercise name yield to the chip. The same
            rule on `currentLabel` made it the screen column's only shrinkable
            child and clipped the whole line on dev 4.15.2. */}
        <Text
          style={[styles.currentLabel, styles.currentLabelInRow]}
          // A ceiling, not a clip: the chip keeps its natural width, so a long
          // name yields and could otherwise run to three or four lines beside
          // a one-line control.
          numberOfLines={2}
        >
          {/* DS-42 — no "Current:" prefix any more: the band heading right
              above already says which exercise this band is about, and the
              prefix was the screen's third way of saying the same thing. */}
          <Text style={styles.currentName}>
            {getExerciseName(current.name)}
          </Text>
        </Text>
        <Chip
          theme={theme}
          hitSlop={{ top: 8, bottom: 8 }}
          // The caret states the fold rather than implying it, and matches the
          // Filters control sitting one line below.
          label={`${chipText} ${disclosure.expanded ? '▴' : '▾'}`}
          expanded={disclosure.expanded}
          // The exercise name rides along: the row used to be one Pressable
          // that named both, and a reader landing straight on the chip would
          // otherwise hear "Machine A" without knowing of what.
          accessibilityLabel={
            known.length > 0
              ? `${getExerciseName(current.name)}, ${chipText}, ${countLabel}`
              : `${getExerciseName(current.name)}, ${t('swap.setMachine', { defaultValue: 'Set machine' })}`
          }
          // The consequence rides WITH the control, not merely under it. On
          // screen the line below is read before the thumb moves; a reader
          // navigating by control would reach this chip and could fire it
          // before ever swiping on to that sentence, so the one piece of copy
          // added to say "your logged sets survive this" would be skippable by
          // exactly the person it protects.
          accessibilityHint={t('swap.currentConsequence', {
            defaultValue:
              'Sets you already logged stay: only the gear they are marked on changes.',
          })}
          onPress={disclosure.toggle}
        />
        {/* EQ-1-GYM-COLDSTART R3 — the ✕, and it does NOT remove anything: it
            opens the question (equipment-model.md §11.7). The gesture lives
            here, on the single callsite that shows the exercise you are ON,
            and never on the candidate rows — those were deliberately
            lightened, and a per-row control on a long list is input fatigue.
            ⚠️ hitSlop is horizontal-asymmetric on purpose: the <Chip> to the
            left carries {top,bottom} slop with NO horizontal spread, so the
            left value may only eat into the 8pt margin between them — 6, so
            2pt stay clear — in a row that has fragile-flex history
            (styles:66-71). 52pt tall, 50pt wide, and no zone shared with the
            chip. */}
        {onDeclareUnavailable ? (
          <IconButton
            theme={theme}
            name="close"
            variant="cancel"
            style={styles.currentUnavailable}
            renderGlyph={(glyph) => (
              <UnseenCue theme={theme} seen={removeSeen}>
                {glyph}
              </UnseenCue>
            )}
            hitSlop={{ top: 8, bottom: 8, left: 6, right: 8 }}
            accessibilityLabel={t('swap.unavailableOpen', {
              defaultValue: 'Gear not available here',
            })}
            accessibilityHint={t('swap.unavailableOpenHint', {
              defaultValue: "Asks which of this exercise's gear is missing.",
            })}
            onPress={() => {
              markRemoveSeen();
              onDeclareUnavailable();
            }}
          />
        ) : null}
      </View>
      {/* DS-42 — what this gesture does to work already logged, stated where
          the gesture is. Same-exercise replace keeps the sets and re-stamps
          the tag (§8.4); saying so is what makes the band distinguishable from
          the one below, which can clear them on a cross-kind swap. */}
      <Text style={styles.consequence}>
        {t('swap.currentConsequence', {
          defaultValue:
            'Sets you already logged stay: only the gear they are marked on changes.',
        })}
      </Text>
      {disclosure.expanded ? (
        <MachineTray
          known={known}
          fallback={fallback}
          {...(pin ? { pin } : {})}
          activeInstance={currentInstance}
          takenValues={takenValues}
          unit={unit}
          theme={theme}
          // Same exercise, different machine: the reducer keeps the logged
          // sets (same kind) and re-stamps the tag on them (§8.4).
          onChoose={(value) => onPick(current, value)}
          // Back to the unnamed default. `null` is an explicit answer here, not
          // an omission: useReplaceExercise's silent-instance fallback only fires
          // resolution when the argument is `undefined`, so passing it clears
          // the tag instead of re-applying the machine just left.
          onClear={() => onPick(current, null)}
        />
      ) : null}
    </View>
  );
}
